import type {useTranslations} from 'next-intl';
import {z} from 'zod';
import {checkIsPhone} from '../helpers/checkIsPhone';
import {isEmail} from '../helpers/isEmail';

const passwordValidation = (t: ReturnType<typeof useTranslations>) =>
  z
    .string()
    .min(8, {message: t('Errors.PasswordMinLength')})
    .regex(/[a-z]/, {message: t('Errors.PasswordLowercase')})
    .regex(/[A-Z]/, {message: t('Errors.PasswordUppercase')})
    .regex(/[0-9]/, {message: t('Errors.PasswordDigit')});

/**
 * @description Схема валидации формы регистрации для email
 */
export const signUpEmailSchema = (t: ReturnType<typeof useTranslations>) =>
  z
    .object({
      email: z
        .string()
        .trim()
        .min(1, {message: t('Errors.InputEmail')})
        .refine(val => isEmail(val), {
          message: t('Errors.EnterValidEmail'),
        }),
      password: passwordValidation(t),
      confirmPassword: z.string().min(1, {message: t('Errors.EnterConfirmPassword')}),
    })
    .refine(data => data.password === data.confirmPassword, {
      path: ['confirmPassword'],
      message: t('Errors.PasswordsDoNotMatch'),
    });

/**
 * @description Схема валидации формы регистрации для телефона
 */
export const signUpPhoneSchema = (t: ReturnType<typeof useTranslations>) =>
  z
    .object({
      phone: z
        .string()
        .trim()
        .min(1, {message: t('Errors.InputPhone')})
        .refine(
          val => {
            const digits = val.replace(/\D/g, '');
            return digits.length >= 7 && digits.length <= 15;
          },
          {
            message: t('Errors.EnterValidPhone'),
          },
        ),
      password: passwordValidation(t),
      confirmPassword: z.string().min(1, {message: t('Errors.EnterConfirmPassword')}),
    })
    .refine(data => data.password === data.confirmPassword, {
      path: ['confirmPassword'],
      message: t('Errors.PasswordsDoNotMatch'),
    });

/**
 * @description Схема валидации формы регистрации: обязательные поля, email ИЛИ телефон
 * @deprecated Использовать signUpEmailSchema или signUpPhoneSchema
 */
export const signUpSchema = (t: ReturnType<typeof useTranslations>) =>
  z
    .object({
      emailOrPhone: z
        .string()
        .trim()
        .min(1, {message: t('Errors.InputEmailOrPhone')})
        .refine(val => isEmail(val) || checkIsPhone(val), {
          message: t('Errors.EnterValidEmailOrPhone'),
        }),
      password: passwordValidation(t),
      confirmPassword: z.string().min(1, {message: t('Errors.EnterConfirmPassword')}),
    })
    .refine(data => data.password === data.confirmPassword, {
      path: ['confirmPassword'],
      message: t('Errors.PasswordsDoNotMatch'),
    });
